import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  withStyles,
  WithStyles,
  Grid,
  Theme
} from '@material-ui/core'
import i18n from 'i18next'
import BinIcon from 'mdi-react/BinIcon'
import ImageIcon from 'mdi-react/ImageIcon'
import { path } from 'ramda'
import { FC } from 'react'
import { connect } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import { Dispatch } from 'redux'
import { openDialog } from '../../../actions/dialogs'
import { OpenDialog, RemoveApartment, Navigate } from '../../../actions/types'
import { Message } from '../../../components/Messages'
import { getApartmentStatusInfo } from '../../../libs/statuses'
import { selectApartmentFilesByApartmentId } from '../../../selectors/apartments'
import {
  selectApplicationsByApartmentId,
  selectNewApplications
} from '../../../selectors/applications'
import { selectShowingsByApartmentId } from '../../../selectors/showings'
import { BucketObject } from '../../../services/storage/types'
import { Apartment, ApartmentStatus } from '../../../types/apartments'
import { Application } from '../../../types/applications'
import { Showing } from '../../../types/showings'
import { StateInterface } from '../../../types/states'
import Dialog from '../../Dialog'
import ApartmentCardButtons from './ApartmentCardButtons'

const styles = ({ breakpoints }: Theme) => ({
  primaryBtn: {
    backgroundColor: '#4f9b44',
    padding: '7px',
    fontSize: '0.8125rem',
    '&:hover': {
      backgroundColor: '#3f7c36'
    }
  },
  primaryBtnOutlined: {
    color: '#4f9b44',
    fontSize: '0.8125rem',
    backgroundColor: '#fff',
    padding: '7px',
    border: '1px solid #4f9b44',
    '&:hover': {
      color: '#3f7c36',
      border: '1px solid #3f7c36'
    }
  },
  card: {
    width: 370,
    maxHeight: 500,
    display: 'inline-block',
    verticalAlign: 'middle',
    margin: '0px 10px 15px',
    position: 'relative' as const,
    [breakpoints.down('sm')]: {
      margin: '0 0 15px',
      width: 400
    }
  },
  icon: {
    position: 'absolute' as const,
    top: '10px',
    right: '10px',
    cursor: 'pointer'
  },
  header1: {
    fontSize: '22px'
  },
  header2: {
    fontSize: '18px'
  },
  cardActions: {
    // float: 'right' as const Adjusted to full width
    padding: '0.8rem 0rem 0rem 0rem'
  },
  defaulMedia: {
    height: 260,
    maxHeight: 260,
    lineHeight: '260px',
    textAlign: 'center' as const,
    [breakpoints.down('sm')]: {
      height: 283,
      lineHeight: '283px'
    }
  },
  defaulMediaIcon: {
    verticalAlign: 'middle' as const,
    color: '#6F6F6F'
  },
  media: {
    objectFit: 'cover' as const,
    height: '260px',
    maxHeight: '260px',
    [breakpoints.down('sm')]: {
      height: 283,
      lineHeight: '283px'
    }
  },
  status: {
    color: '#fff',
    padding: '0.5rem 0',
    textAlign: 'center' as const,
    margin: '0'
  },
  cardContent: {
    padding: '0.8rem 0.5rem 0.8rem 0.8rem'
  }
})

/** Props this component take from UserApartments.tsx */
interface OwnProps {
  removeApartment: RemoveApartment
  navigate: Navigate
  t: i18n.TFunction
  apartment: Apartment
  apartmentId: string
}

/** state props see map state to props */
interface StateProps {
  files: BucketObject[]
  applications: Application[]
  showings: Showing[]
}

/** props in mapDispatchToProps */
interface DispatchProps {
  openDialogAction: OpenDialog
}

type ComponentProps = OwnProps & StateProps & DispatchProps & WithStyles

const ApartmentCard: FC<ComponentProps> = ({
  classes,
  t,
  removeApartment,
  apartment,
  navigate,
  files,
  openDialogAction,
  applications,
  showings
}) => {
  const {
    apartmentId,
    metadata: { media },
    status,
    userId
  } = apartment
  const hasApplications = applications.length > 0
  const hasNewApplications = selectNewApplications(applications).length > 0
  const apartmentCardButtonsProps = {
    apartment,
    navigate,
    // TODO: might need to check for specific statuses
    hasApplications,
    classes,
    t
  }
  const { statusColor, statusTranslation } = getApartmentStatusInfo(
    apartment,
    hasNewApplications,
    media
  )
  return (
    <Card className={classes.card}>
      {status === ApartmentStatus.created && (
        <span
          className={classes.icon}
          onClick={() =>
            openDialogAction(`ConfirmDeleteApartmentDialog-${apartmentId}`)
          }
        >
          <BinIcon />
        </span>
      )}
      <Dialog
        dialogId={`ConfirmDeleteApartmentDialog-${apartmentId}`}
        title={t('apartments.delete.label')}
        noOpenDialogButton
        confirmDialogSubmitAction={() => removeApartment(apartmentId)}
        Content={
          <Message
            message={t('apartments.delete.confirmMessage', {
              address: path(['information', 'address'], apartment)
            })}
          />
        }
      />
      {files && files.length > 0 ? (
        <CardMedia
          component='img'
          image={`${files[0].url}`}
          className={classes.media}
        />
      ) : (
        <div className={classes.defaulMedia}>
          <ImageIcon className={classes.defaulMediaIcon} size='100px' />
        </div>
      )}
      <RouterLink
        to={`/users/${userId}/apartments/${apartmentId}/overview`}
        style={{ textDecoration: 'none' }}
      >
        <Typography
          className={classes.status}
          style={{ backgroundColor: statusColor }}
        >
          {t(`apartments.status.${statusTranslation}`)}
        </Typography>
      </RouterLink>
      <CardContent className={classes.cardContent}>
        <Typography
          variant='body1'
          component='h1'
          gutterBottom
          className={classes.header1}
        >
          {path(['information', 'address'], apartment) || 'No title'}
        </Typography>
        <Grid container>
          <Grid item sm={8}>
            <Typography variant='body1' color='textSecondary'>
              {apartment && apartment.information && apartment.confirmation ? (
                <>
                  {apartment?.information?.rooms} +{' '}
                  {apartment.information?.kitchen} |{' '}
                  {apartment.information?.squareMeters} m<sup>2</sup> |{' '}
                  {apartment.confirmation.price}€
                </>
              ) : (
                'No data filled'
              )}
            </Typography>
          </Grid>
        </Grid>
        <ApartmentCardButtons {...apartmentCardButtonsProps} />
      </CardContent>
    </Card>
  )
}

const mapStateToProps = (state: StateInterface, { apartmentId }: OwnProps) => ({
  files: selectApartmentFilesByApartmentId(apartmentId)(state),
  applications: selectApplicationsByApartmentId(apartmentId)(state),
  showings: selectShowingsByApartmentId(apartmentId)(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  openDialogAction: (id: string) => dispatch(openDialog(id))
})

export default connect<StateProps, DispatchProps, OwnProps, StateInterface>(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(ApartmentCard))
